import { useContext } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import { pathProfileFunctionsWrapper } from 'app/shared/path_profile/path_profile';
import { Position } from 'app/agriculture/queries/types/geometry/Position';

const DEFAULT_RANGE = 1000;

export function useFlyTo() {
    const { mapService, terrainService } = useContext(ServicesContext);

    function getScreenCenter() {
        const screenCenter: Cesium.Cartesian2 = new Cesium.Cartesian2(
            mapService.viewer.container.clientWidth / 2,
            mapService.viewer.container.clientHeight / 2,
        );
        const pickRay = mapService.viewer.scene.camera.getPickRay(screenCenter);
        if (pickRay) {
            return mapService.viewer.scene.globe.pick(pickRay, mapService.viewer.scene);
        }
        return undefined;
    }

    function getCameraDistanceToPosition(position: Cesium.Cartographic): number {
        const positionAsCartesian: Cesium.Cartesian3 = Cesium.Cartesian3.fromRadians(
            position.longitude,
            position.latitude,
            position.height,
        );
        return Cesium.Cartesian3.distance(mapService.viewer.camera.positionWC, positionAsCartesian);
    }

    function boundingSphereFromCartographic(position: Cesium.Cartographic): Cesium.BoundingSphere {
        return new Cesium.BoundingSphere(
            Cesium.Cartesian3.fromRadians(position.longitude, position.latitude, position.height),
            0,
        );
    }

    async function flyTo([longitude, latitude]: Position) {
        let flyToDestination: Cesium.Cartographic | undefined = Cesium.Cartographic.fromDegrees(
            longitude,
            latitude,
        );
        const sphere = boundingSphereFromCartographic(flyToDestination);
        const { pitch, heading } = mapService.viewer.camera;

        const screenCenter = getScreenCenter();

        let screenCenterPosition: Cesium.Cartographic | undefined =
            screenCenter == null
                ? new Cesium.Cartographic()
                : Cesium.Cartographic.fromCartesian(screenCenter);

        if (terrainService.mapTerrainEnabled) {
            [flyToDestination, screenCenterPosition] =
                await pathProfileFunctionsWrapper.fetchTerrainHeights(
                    [flyToDestination, screenCenterPosition],
                    terrainService.cesiumTerrainProvider,
                    terrainService,
                    true,
                );
        }

        mapService.viewer.camera.flyToBoundingSphere(sphere, {
            offset: new Cesium.HeadingPitchRange(
                heading,
                pitch > 0 ? -Math.PI / 2 : pitch,
                screenCenter == null
                    ? DEFAULT_RANGE
                    : getCameraDistanceToPosition(Cesium.Cartographic.fromCartesian(screenCenter)),
            ),
            duration: 1,
        });
    }

    return { flyTo };
}
